import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "../../../hooks/useSession/useSession";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";

const FORETHOUGHT_CONFIG = {
    SCRIPT_ID: "forethought-widget-embed-script",
    WIDGET_IFRAME_ID: "forethought-chat",
    SCRIPT_SRC: "https://solve-widget.forethought.ai/embed.js"
};

const SKIPPED_ROUTES = ["/callback", "/logout"];

const removeWidget = () => {
    (window as Window & { Forethought?: (...args: string[]) => void }).Forethought?.("widget", "destroy");
    document.getElementById(FORETHOUGHT_CONFIG.WIDGET_IFRAME_ID)?.remove();
    document.getElementById(FORETHOUGHT_CONFIG.SCRIPT_ID)?.remove();
};

export const ForethoughtWidget = () => {
    const { accessToken, isAuthenticated } = useSession();
    const { enabled } = useFeatureFlag(Flags.ENABLE_FORETHOUGHT_WIDGET);
    const { pathname } = useLocation();
    const apiKey = process.env.REACT_APP_FORETHOUGHT_API_KEY;

    useEffect(() => {
        const canInject = enabled && isAuthenticated && !!accessToken && !!apiKey && !SKIPPED_ROUTES.includes(pathname);

        if (!canInject) {
            removeWidget();
            return;
        }

        const existing = document.getElementById(FORETHOUGHT_CONFIG.SCRIPT_ID);
        if (existing) {
            if (existing.getAttribute("data-ft-Login-Token") !== accessToken) {
                existing.setAttribute("data-ft-Login-Token", accessToken);
            }
            return;
        }

        const script = document.createElement("script");
        script.src = FORETHOUGHT_CONFIG.SCRIPT_SRC;
        script.async = true;
        script.id = FORETHOUGHT_CONFIG.SCRIPT_ID;
        script.setAttribute("data-api-key", apiKey);
        script.setAttribute(
            "data-ft-Impact-Severity-v2",
            "AVAILABLE OPTIONS: Severity 1, Severity 2, Severity 3, Severity 4"
        );
        script.setAttribute("data-ft-Incident-Severity", "AVAILABLE OPTIONS: Severity 1, Severity 2");
        script.setAttribute("data-ft-Login-Token", accessToken);
        script.setAttribute("data-ft-workflow-tag", "opti_id");
        script.setAttribute("offset-y", "5rem");
        script.setAttribute(
            "data-ft-Optimizely-Product",
            "AVAILABLE OPTIONS: Analytics (NetSpring), Campaign, CMS / Commerce (B2C) (Not Optimizely hosted), Configured Commerce (B2B), Connect Platform (OCP), Content Graph, Content Marketing Platform (CMP), Content Recommendations, Data Platform (ODP), Digital Asset Management (DAM), Ektron, Everweb (legacy Optimizely hosting), Experimentation, I do not know, None of these, Opal, Optimizely hosted CMS (DXP PaaS), Optimizely hosted CMS (SaaS), Optimizely hosted Commerce Connect (B2C) (DXP PaaS), Product Information Management (PIM), Product Recommendations, Search & Navigation"
        );

        document.body.appendChild(script);
    }, [enabled, isAuthenticated, accessToken, apiKey, pathname]);

    useEffect(() => removeWidget, []);

    return null;
};

ForethoughtWidget.displayName = "ForethoughtWidget";
